import { type FC, useCallback, useEffect, useState } from 'react';

import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';

import { formatVideoLength, formatVideoLimits } from '@/video/formatVideoLimits';
import { VideoCardShell } from '@/video/VideoCardShell';
import { VideoPlayer } from '@/video/VideoPlayer';
import { VideoStatusCard } from '@/video/VideoStatusCard';
import { VideoThumbnailPicker } from '@/video/VideoThumbnailPicker';

import type { VideoStatusSnapshot, VideoThumbnails } from '../../../../../types';
import { COMMUNITY_VIDEO_UPLOAD_LIMITS, tr } from '../../../constants';
import { useVideoUploadContext } from '../../../videoUploadContext';
import type { VideoOptions } from '../Video';

// Inline, not `aspect-video`: Orchid's Tailwind theme replaces aspectRatio, so the
// utility is not generated there and absolutely positioned content collapses to 0.
const VIDEO_ASPECT = { aspectRatio: '16 / 9' };

// Resolves once the image is in the browser cache (or failed to load), so a poster
// can switch URLs without flashing blank while the new one downloads.
const preloadImage = (url: string | null): Promise<void> =>
  new Promise((resolve) => {
    if (!url) {
      resolve();
      return;
    }
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = url;
  });

const RemoveButton: FC<{ onRemove: () => void; label: string }> = ({ onRemove, label }) => (
  <button
    type="button"
    onClick={onRemove}
    className="absolute right-2 top-2 inline-flex h-6 items-center gap-1 rounded-full bg-black/70 pl-2.5 pr-2 text-xs font-medium text-white shadow-sm transition-colors hover:bg-white hover:text-black"
  >
    {label}
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  </button>
);

export const VideoView: FC<NodeViewProps> = ({ node, editor, deleteNode, extension }) => {
  const videoId = typeof node.attrs.videoId === 'string' ? node.attrs.videoId : '';
  const {
    subscribeToStatus,
    onRetry,
    resolvePlayback,
    loadThumbnails,
    selectThumbnail,
    uploadThumbnail,
    onUnplayableChange,
    uploadLimits = COMMUNITY_VIDEO_UPLOAD_LIMITS,
    locale,
    translations,
  } = extension.options as VideoOptions;
  const upload = useVideoUploadContext();

  // Before the first snapshot the video is in flight, so default to processing.
  const [snapshot, setSnapshot] = useState<VideoStatusSnapshot>({ type: 'status', status: 'transcoding', revision: 0 });
  // Counts thumbnails updates, so each one reloads the candidates.
  const [thumbnailsUpdates, setThumbnailsUpdates] = useState(0);
  const status = snapshot.status;
  const [retrying, setRetrying] = useState(false);
  // The thumbnail is the clickable preview; the player only mounts once the viewer
  // presses play, so nothing streams until it is asked for.
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!videoId || !subscribeToStatus) return;

    const unsubscribe = subscribeToStatus(videoId, (update) => {
      if (update.type === 'thumbnails') {
        setThumbnailsUpdates((count) => count + 1);
        return;
      }
      // Drop stale or out-of-order messages: only advance on a newer revision.
      setSnapshot((current) => (update.revision > current.revision ? update : current));
    });

    return unsubscribe;
  }, [videoId, subscribeToStatus]);

  // The cleanup clears the report when the video is removed or a retry restarts processing.
  const unplayable = Boolean(videoId) && (status === 'failed' || status === 'blocked');
  useEffect(() => {
    if (!unplayable || !onUnplayableChange) return;
    onUnplayableChange(true);
    return () => onUnplayableChange(false);
  }, [unplayable, onUnplayableChange]);

  const removeLabel = tr(translations, 'video.remove');
  // The limits the Add video hint shows, so a rejection names the one it broke, and
  // the length of a picked video that broke the duration limit.
  const withLimits = (text: string, durationInMs?: number | null) => {
    const { size, duration } = formatVideoLimits(uploadLimits, locale);
    const filled = text.replace('{{size}}', size).replace('{{duration}}', duration);
    return durationInMs ? filled.replace('{{length}}', formatVideoLength(durationInMs, locale)) : filled;
  };
  const handleDelete = useCallback(() => deleteNode(), [deleteNode]);

  // Removing a placeholder must also abort the in-flight upload behind it.
  const handleCancelUpload = useCallback(() => {
    upload?.cancel();
    editor.commands.removeProvisionalVideo();
  }, [upload, editor]);

  const handleRetry = useCallback(async () => {
    if (!onRetry || !videoId || retrying) return;
    setRetrying(true);
    try {
      await onRetry(videoId);
    } finally {
      setRetrying(false);
    }
  }, [onRetry, videoId, retrying]);

  const [thumbnails, setThumbnails] = useState<VideoThumbnails | null>(null);

  const reloadThumbnails = useCallback(async () => {
    if (!videoId || !loadThumbnails) return;
    const next = await loadThumbnails(videoId);
    if (!next) return;
    setThumbnails(next);
    // The links stay the same within a signing window, so opening the picker
    // shows the previews from the browser cache.
    for (const candidate of next.candidates ?? []) void preloadImage(candidate.previewUrl);
  }, [videoId, loadThumbnails]);

  // A reopened composer has no thumbnails update to wait for, so they also load on mount.
  useEffect(() => {
    void reloadThumbnails();
  }, [reloadThumbnails]);

  useEffect(() => {
    if (thumbnailsUpdates > 0) void reloadThumbnails();
  }, [thumbnailsUpdates, reloadThumbnails]);

  // Optimistic: show the chosen frame at once and restore the previous one on failure.
  // A change also resets the player to the clickable preview, so the new poster shows
  // immediately and playback restarts on the next click.
  const handleSelectThumbnail = useCallback(
    async (index: number): Promise<boolean> => {
      if (!videoId || !selectThumbnail) return false;
      const previous = thumbnails;
      const candidate = previous?.candidates?.find((option) => option.index === index);
      if (previous && candidate) setThumbnails({ ...previous, selectedIndex: index, selectedUrl: candidate.url });
      setPlaying(false);
      const next = await selectThumbnail(videoId, index);
      if (next) await preloadImage(next.selectedUrl);
      setThumbnails(next ?? previous);
      return next !== null;
    },
    [videoId, selectThumbnail, thumbnails],
  );

  const handleUploadThumbnail = useCallback(
    async (file: File): Promise<boolean> => {
      if (!videoId || !uploadThumbnail) return false;
      const previous = thumbnails;
      const previewUrl = URL.createObjectURL(file);
      setThumbnails({ candidates: previous?.candidates ?? [], selectedIndex: null, selectedUrl: previewUrl });
      setPlaying(false);
      const next = await uploadThumbnail(videoId, file);
      if (next) await preloadImage(next.selectedUrl);
      setThumbnails(next ?? previous);
      URL.revokeObjectURL(previewUrl);
      return next !== null;
    },
    [videoId, uploadThumbnail, thumbnails],
  );

  const poster = thumbnails?.selectedUrl ?? undefined;
  const candidates = thumbnails?.candidates;
  // Hidden until generation finishes; if it produced no frames, only the upload tile shows.
  const picker =
    editor.isEditable && selectThumbnail && candidates && (candidates.length > 0 || uploadThumbnail) ? (
      <VideoThumbnailPicker
        candidates={candidates}
        selectedIndex={thumbnails?.selectedIndex ?? null}
        onSelect={handleSelectThumbnail}
        onUpload={uploadThumbnail ? handleUploadThumbnail : undefined}
        onOpen={() => void reloadThumbnails()}
        translations={translations}
      />
    ) : null;

  // Placeholder: the upload is still running or failed before it produced an id.
  // Its progress, finishing state, transfer/completion errors, cancel, and retry
  // all come from the shared upload controls.
  if (!videoId) {
    const state = upload?.state;

    // A file the upload rejected shows why, like a video that failed processing.
    if (state?.phase === 'error' && state.errorKind === 'validation') {
      return (
        <NodeViewWrapper
          as="div"
          className="community-editor-video-wrapper relative"
          data-drag-handle
          contentEditable={false}
        >
          <VideoStatusCard
            status="failed"
            message={withLimits(tr(translations, state.errorKey ?? 'video.status_failed'), state.durationInMs)}
            hint={tr(translations, 'video.remove_to_post')}
            translations={translations}
          />
          <RemoveButton onRemove={handleCancelUpload} label={removeLabel} />
        </NodeViewWrapper>
      );
    }

    const showError = state?.phase === 'error';

    return (
      <NodeViewWrapper
        as="div"
        className="community-editor-video-wrapper relative"
        data-drag-handle
        contentEditable={false}
      >
        <VideoCardShell status="uploading">
          {showError ? (
            <>
              <span className="text-sm text-red-500" role="alert">
                {tr(translations, state?.errorKey ?? 'video.error_transfer')}
              </span>
              {upload && (
                <button
                  type="button"
                  onClick={upload.retry}
                  className="rounded-full bg-black/70 px-3 py-1 text-xs text-white transition-colors hover:bg-black"
                >
                  {tr(translations, 'video.retry')}
                </button>
              )}
            </>
          ) : (
            <>
              <span className="text-sm">
                {tr(translations, state?.phase === 'confirming' ? 'video.processing' : 'video.uploading')}
              </span>
              <div className="h-2 w-2/3 overflow-hidden rounded-full bg-black/10">
                <div
                  className="h-full rounded-full bg-black/60 transition-[width]"
                  style={{ width: `${state?.progress ?? 0}%` }}
                  role="progressbar"
                  aria-label={tr(translations, 'video.uploading')}
                  aria-valuenow={state?.progress ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </>
          )}
        </VideoCardShell>
        <RemoveButton onRemove={handleCancelUpload} label={tr(translations, 'video.cancel')} />
      </NodeViewWrapper>
    );
  }

  // Terminal, non-playable states keep the status card with their own affordances.
  if (status === 'failed' || status === 'blocked') {
    const failureMessage = snapshot.reason
      ? withLimits(tr(translations, `video.failed_${snapshot.reason}`))
      : undefined;

    return (
      <NodeViewWrapper
        as="div"
        className="community-editor-video-wrapper relative"
        data-drag-handle
        contentEditable={false}
      >
        <VideoStatusCard
          status={status}
          videoId={videoId}
          onRetry={snapshot.retryable !== false && onRetry ? handleRetry : undefined}
          retrying={retrying}
          message={failureMessage}
          hint={editor.isEditable ? tr(translations, 'video.remove_to_post') : undefined}
          translations={translations}
        />
        {editor.isEditable && <RemoveButton onRemove={handleDelete} label={removeLabel} />}
      </NodeViewWrapper>
    );
  }

  // Processing and ready both lead with the chosen thumbnail. While transcoding it
  // carries a processing overlay; once ready it becomes a play button, and only
  // then does pressing it mount the player and start streaming.
  const canPlay = status === 'ready' && Boolean(resolvePlayback);

  // The card drags as a whole, except while the player is up so scrubbing stays scrubbing.
  return (
    <NodeViewWrapper
      as="div"
      className="community-editor-video-wrapper relative"
      data-drag-handle={playing ? undefined : ''}
      contentEditable={false}
    >
      {playing && canPlay && resolvePlayback ? (
        <div className="w-full overflow-hidden rounded-xl bg-black" style={VIDEO_ASPECT}>
          <VideoPlayer
            videoId={videoId}
            resolvePlayback={resolvePlayback}
            poster={poster}
            autoPlay
            translations={translations}
            className="w-full"
          />
        </div>
      ) : canPlay ? (
        <div
          className="relative w-full overflow-hidden rounded-xl bg-black"
          style={VIDEO_ASPECT}
          data-video-id={videoId}
          data-video-status={status}
        >
          {poster && <img src={poster} alt="" className="absolute inset-0 size-full object-cover" />}
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={tr(translations, 'video.play')}
            className="absolute inset-0 m-auto flex size-14 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-black/80"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>
      ) : (
        <VideoStatusCard status="processing" videoId={videoId} poster={poster} translations={translations} />
      )}
      {editor.isEditable && (
        <>
          {picker}
          <RemoveButton onRemove={handleDelete} label={removeLabel} />
        </>
      )}
    </NodeViewWrapper>
  );
};
